"use client";

import {
  createDefaultFrontendContainer,
  FrontendContainer,
} from "@/infrastructure/di/FrontendContainer";
import { getAuthHeaders } from "@/infrastructure/http/authHeaders";
import { createRecordsGateway } from "@/interface-adapters/records/services/RecordsGateway";
import type {
  UploadAudioFileRequestDTO,
  UploadTextFileRequestDTO,
  CheckSpaceResponseDTO,
} from "@/application/records/dtos/UploadDTO";
import type { UpdateRecordsRequest } from "@/lib/recordsApi";

export type UploadAudioFileRequest = UploadAudioFileRequestDTO;
export type UploadTextFileRequest = UploadTextFileRequestDTO;
export type CheckSpaceResponse = CheckSpaceResponseDTO;

export interface UploadResult {
  id: string;
  name: string;
  file_path: string;
}

export interface UpdateRecordFilePathRequest {
  record_id: string;
  file_path: string;
}

class FileUploadDependencies {
  private static container: FrontendContainer | null = null;
  private static gateway = null as ReturnType<typeof createRecordsGateway> | null;

  static getContainer(): FrontendContainer {
    if (!this.container) {
      this.container = createDefaultFrontendContainer({
        authHeadersProvider: getAuthHeaders,
      });
    }
    return this.container;
  }

  static getGateway() {
    if (!this.gateway) {
      this.gateway = createRecordsGateway(this.getContainer());
    }
    return this.gateway;
  }

  static getInsightAdapter() {
    return this.getContainer().resolveInsightApiAdapter();
  }
}

/**
 * File Upload Service
 * Provides methods for uploading files to Insight backend via infrastructure adapters.
 */
export class FileUploadService {
  private static instance: FileUploadService;

  private constructor() {}

  public static getInstance(): FileUploadService {
    if (!FileUploadService.instance) {
      FileUploadService.instance = new FileUploadService();
    }
    return FileUploadService.instance;
  }

  async uploadAudioFile(
    request: UploadAudioFileRequest,
    projectId?: string
  ): Promise<UploadResult> {
    const adapter = FileUploadDependencies.getInsightAdapter();
    const requestWithProject = projectId
      ? { ...request, project_id: projectId }
      : request;
    const response = await adapter.uploadAudioFile(requestWithProject);
    return {
      id: response.id,
      name: request.record_name,
      file_path: request.file_path,
    };
  }

  async uploadTextFile(
    request: UploadTextFileRequest,
    projectId?: string
  ): Promise<UploadResult> {
    const adapter = FileUploadDependencies.getInsightAdapter();
    const requestWithProject = projectId
      ? { ...request, project_id: projectId }
      : request;
    const response = await adapter.uploadTextFile(requestWithProject);
    return {
      id: response.id,
      name: request.record_name,
      file_path: "",
    };
  }

  async checkSpace(fileSizeInMb: number): Promise<boolean> {
    const adapter = FileUploadDependencies.getInsightAdapter();
    const response = await adapter.checkSpace(fileSizeInMb);
    return response.is_enough === "True";
  }

  async updateRecordFilePath(
    request: UpdateRecordFilePathRequest
  ): Promise<void> {
    const gateway = FileUploadDependencies.getGateway();
    const updateRequest: UpdateRecordsRequest = {
      change_names: [],
      for_archive: [],
      for_delete: [],
      delete_link: [request.record_id],
    };
    await gateway.updateRecords(updateRequest);
  }
}

export const fileUploadService = FileUploadService.getInstance();
